// src/taskpane/services/translator.ts
import {
  ExtractedTextItem,
  TranslationProgressCallback,
  TranslationResult,
  AddinSettings,
} from "./types";

const TRANSLATOR_API_BASE = "https://api.cognitive.microsofttranslator.com";
const API_VERSION = "3.0";
const MAX_CHARS_PER_REQUEST = 50000;
const MAX_ITEMS_PER_REQUEST = 1000;

export async function translateBatch(
  texts: string[],
  targetLang: string,
  sourceLang: string | null,
  apiKey: string,
  apiRegion: string
): Promise<string[]> {
  const params = new URLSearchParams({
    "api-version": API_VERSION,
    to: targetLang,
  });
  if (sourceLang && sourceLang !== "auto") {
    params.set("from", sourceLang);
  }

  const url = `${TRANSLATOR_API_BASE}/translate?${params.toString()}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json; charset=UTF-8",
    "Ocp-Apim-Subscription-Key": apiKey,
  };
  if (apiRegion && apiRegion !== "global") {
    headers["Ocp-Apim-Subscription-Region"] = apiRegion;
  }

  const body = texts.map((text) => ({ Text: text }));

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Azure Translator API 错误 (${response.status}): ${errorText}`
    );
  }

  const data = await response.json();
  return data.map(
    (item: { translations: { text: string }[] }) => item.translations[0].text
  );
}

/**
 * 将文本项列表按大小分批
 */
function batchItems(items: ExtractedTextItem[]): ExtractedTextItem[][] {
  const batches: ExtractedTextItem[][] = [];
  let currentBatch: ExtractedTextItem[] = [];
  let currentChars = 0;

  for (const item of items) {
    if (
      currentBatch.length >= MAX_ITEMS_PER_REQUEST ||
      currentChars + item.originalText.length > MAX_CHARS_PER_REQUEST
    ) {
      if (currentBatch.length > 0) {
        batches.push(currentBatch);
      }
      currentBatch = [];
      currentChars = 0;
    }
    currentBatch.push(item);
    currentChars += item.originalText.length;
  }

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
}

/**
 * 翻译所有提取的文本项
 */
export async function translateAllText(
  items: ExtractedTextItem[],
  targetLang: string,
  sourceLang: string,
  settings: AddinSettings,
  onProgress?: TranslationProgressCallback
): Promise<TranslationResult> {
  // 过滤掉空文本
  const itemsToTranslate = items.filter((item) => item.originalText.trim());
  const skippedCount = items.length - itemsToTranslate.length;

  if (itemsToTranslate.length === 0) {
    return {
      items,
      successCount: 0,
      failCount: 0,
      skippedCount,
    };
  }

  // 分批处理
  const batches = batchItems(itemsToTranslate);
  let completedCount = 0;

  for (const batch of batches) {
    try {
      const texts = batch.map((item) => item.originalText);
      const translations = await translateBatch(
        texts,
        targetLang,
        sourceLang === "auto" ? null : sourceLang,
        settings.apiKey,
        settings.apiRegion
      );

      // 将翻译结果写回对应项
      for (let i = 0; i < batch.length; i++) {
        batch[i].translatedText = translations[i];
        batch[i].translated = true;
        batch[i].error = null;
      }
    } catch (error) {
      // 批次失败时，标记该批次所有项为失败
      const errorMsg =
        error instanceof Error ? error.message : String(error);
      for (const item of batch) {
        item.translated = false;
        item.error = errorMsg;
      }
    }

    completedCount += batch.length;
    if (onProgress) {
      onProgress(
        completedCount,
        itemsToTranslate.length,
        batch[batch.length - 1].originalText.substring(0, 50)
      );
    }
  }

  const successCount = itemsToTranslate.filter((item) => item.translated).length;
  const failCount = itemsToTranslate.filter((item) => !item.translated).length;

  return {
    items,
    successCount,
    failCount,
    skippedCount,
  };
}
